import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "triptracker.chunkReloadAt";
/** Don't reload again within this window — a chunk that is really gone
 *  shouldn't turn into a reload loop. */
const RELOAD_GUARD_MS = 30_000;

/**
 * Import a code-split chunk, recovering once from a deploy that removed it.
 *
 * The service worker precaches every chunk, so offline this always resolves
 * from cache. The one way it fails is a page that was open across a deploy:
 * it still asks for last version's file names, which the new service worker
 * has cleaned out and hosting no longer serves. Reloading picks up the new
 * index.html and its chunks, so do that — once.
 */
export function importWithReload<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch((err: unknown) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    } catch {
      // Storage unavailable: fall through and reload anyway.
    }
    if (Date.now() - last > RELOAD_GUARD_MS) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        // Nothing to remember it in; the guard just won't apply.
      }
      window.location.reload();
      // Never settles: the page is going away.
      return new Promise<T>(() => {});
    }
    throw err;
  });
}

/** `React.lazy` over `importWithReload`, for a module's default export. */
export function lazyWithReload<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(() => importWithReload(factory));
}
